<script setup lang="ts">
/**
 * Module: web/src/components/admin/UserEditorModal.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.3.0 (decision #176: assisted_entry_review cap label -> 'Entry Queue (assisted-entry review)')
 * Date: 2026-07-10
 *
 * v1.2.0: HelpTips wired (doc #853 backlog): admin.goto_extension +
 * admin.linked_person on the Profile tab fields.
 *
 * Tabbed user editor (doc 04 sect 9.2 -- pattern: firstline CallDetailModal):
 *   Profile -- name, email, goto_extension, active, optional person link
 *   Roles   -- MULTI-SELECT checkboxes (a user holds a SET of roles) with a
 *              LIVE UNION preview of the resulting default caps
 *   Access  -- three-state override toggles per overridable capability
 *              (Inherit / Grant / Deny -- deny wins over the role union) +
 *              live effective-caps preview
 *   Audit   -- this user's recent auth events
 *
 * Create mode (userId=null): POST /admin/users, then PUT overrides if any set.
 * Edit mode: PATCH profile + PUT roles + PUT overrides.
 *
 * v1.1.0 (P9 R3, contract user-onboarding-mail sect 3): create mode gains a
 * "Send welcome email" checkbox (default UNCHECKED) -> send_welcome:true on
 * the POST; the backend then emails the temp password (secure random, not
 * the name pattern) and reports welcome_sent, re-emitted as 'welcome-result'
 * so the parent can toast success / retry guidance.
 */
import { computed, ref, watch } from 'vue';
import { apiGet, apiPatch, apiPost, apiPut, unwrapList, ApiError } from '@/composables/useApi';
import {
  OVERRIDABLE_CAPS,
  type AdminPerson,
  type AdminRole,
  type AdminUser,
  type AuditEvent,
  type CapabilityOverride,
  type OverrideEffect,
} from '@/types/api';
import { fmtDateTime, fmtDetail } from '@/lib/format';
import BaseModal from '@/components/BaseModal.vue';
import HelpTip from '@/components/HelpTip.vue';

const props = defineProps<{
  open: boolean;
  /** null = create a new user. */
  userId: number | null;
  roles: AdminRole[];
  people: AdminPerson[];
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved'): void;
  /** A create/reset returned a one-time temp password to hand over. */
  (e: 'temp-password', password: string): void;
  /** Create requested send_welcome -- did the welcome mail go out? */
  (e: 'welcome-result', payload: { sent: boolean; email: string }): void;
}>();

type TabKey = 'profile' | 'roles' | 'access' | 'audit';
type OverrideState = 'inherit' | OverrideEffect;

const tab = ref<TabKey>('profile');
const loading = ref(false);
const saving = ref(false);
const errorMessage = ref('');

// ---- form state ------------------------------------------------------------
const name = ref('');
const email = ref('');
const gotoExtension = ref('');
const active = ref(true);
const personId = ref<number | null>(null);
/** Create-only: email the temp password (contract sect 3; default OFF). */
const sendWelcome = ref(false);
const selectedRoles = ref<string[]>([]);
const overrideState = ref<Record<string, OverrideState>>({});
const auditEvents = ref<AuditEvent[]>([]);
const serverEffectiveCaps = ref<string[]>([]);

const isCreate = computed(() => props.userId === null);

const tabs: { key: TabKey; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'roles', label: 'Roles' },
  { key: 'access', label: 'Access' },
  { key: 'audit', label: 'Audit' },
];
const visibleTabs = computed(() => (isCreate.value ? tabs.filter((t) => t.key !== 'audit') : tabs));

// ---- live capability math (doc 04 sect 3.5) --------------------------------
/** UNION of the selected roles' default caps. */
const roleUnion = computed<string[]>(() => {
  const union = new Set<string>();
  for (const key of selectedRoles.value) {
    const role = props.roles.find((r) => r.key === key);
    for (const cap of role?.capabilities ?? []) union.add(cap);
  }
  return [...union].sort();
});

/** (union ∪ grants) − denies -- deny always wins. */
const effectivePreview = computed<string[]>(() => {
  const caps = new Set(roleUnion.value);
  for (const [cap, state] of Object.entries(overrideState.value)) {
    if (state === 'grant') caps.add(cap);
    if (state === 'deny') caps.delete(cap);
  }
  return [...caps].sort();
});

// ---- open/reset ------------------------------------------------------------
function resetForm() {
  tab.value = 'profile';
  errorMessage.value = '';
  name.value = '';
  email.value = '';
  gotoExtension.value = '';
  active.value = true;
  personId.value = null;
  sendWelcome.value = false;
  selectedRoles.value = [];
  overrideState.value = Object.fromEntries(OVERRIDABLE_CAPS.map((c) => [c, 'inherit' as OverrideState]));
  auditEvents.value = [];
  serverEffectiveCaps.value = [];
}

/** GET /admin/users/{id} returns user + overrides + effective; tolerate flat or {user:...} shape. */
function normalizeDetail(raw: unknown): { user: AdminUser; overrides: CapabilityOverride[]; effective: string[] } {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const user = (typeof obj.user === 'object' && obj.user !== null ? obj.user : obj) as AdminUser;
  const overrides = Array.isArray(obj.overrides) ? (obj.overrides as CapabilityOverride[]) : [];
  const effective = Array.isArray(obj.effective_caps) ? (obj.effective_caps as string[]) : [];
  return { user, overrides, effective };
}

async function loadUser(id: number) {
  loading.value = true;
  try {
    const detail = normalizeDetail(await apiGet<unknown>(`/admin/users/${id}`));
    name.value = detail.user.name ?? '';
    email.value = detail.user.email ?? '';
    gotoExtension.value = detail.user.goto_extension ?? '';
    active.value = detail.user.active !== false;
    personId.value = detail.user.person_id ?? null;
    selectedRoles.value = [...(detail.user.roles ?? [])];
    for (const o of detail.overrides) {
      if (o.capability in overrideState.value) overrideState.value[o.capability] = o.effect;
    }
    serverEffectiveCaps.value = detail.effective;
  } catch {
    errorMessage.value = 'Could not load this user.';
  } finally {
    loading.value = false;
  }
}

async function loadAudit(id: number) {
  try {
    // Contract exposes GET /admin/audit?limit= only -- filter to this user client-side.
    const body = await apiGet<unknown>('/admin/audit', 'limit=200');
    auditEvents.value = unwrapList<AuditEvent>(body, ['events', 'audit', 'rows']).filter((ev) => ev.user_id === id);
  } catch {
    auditEvents.value = [];
  }
}

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    resetForm();
    if (props.userId !== null) {
      void loadUser(props.userId);
      void loadAudit(props.userId);
    }
  },
  { immediate: true },
);

// ---- save ------------------------------------------------------------------
function toggleRole(key: string) {
  selectedRoles.value = selectedRoles.value.includes(key)
    ? selectedRoles.value.filter((r) => r !== key)
    : [...selectedRoles.value, key];
}

function overridesPayload(): CapabilityOverride[] {
  return Object.entries(overrideState.value)
    .filter(([, state]) => state !== 'inherit')
    .map(([capability, state]) => ({ capability, effect: state as OverrideEffect }));
}

async function save() {
  if (saving.value) return;
  errorMessage.value = '';
  if (!name.value.trim() || !email.value.trim()) {
    errorMessage.value = 'Name and email are required.';
    tab.value = 'profile';
    return;
  }
  if (selectedRoles.value.length === 0) {
    errorMessage.value = 'Select at least one role.';
    tab.value = 'roles';
    return;
  }
  saving.value = true;
  try {
    if (isCreate.value) {
      const created = await apiPost<Record<string, unknown>>('/admin/users', {
        email: email.value.trim(),
        name: name.value.trim(),
        roles: selectedRoles.value,
        ...(gotoExtension.value.trim() ? { goto_extension: gotoExtension.value.trim() } : {}),
        ...(personId.value !== null ? { person_id: personId.value } : {}),
        ...(sendWelcome.value ? { send_welcome: true } : {}),
      });
      const createdUser = (created?.user ?? created ?? {}) as Record<string, unknown>;
      const newId = typeof createdUser.id === 'number' ? createdUser.id : null;
      const overrides = overridesPayload();
      if (newId !== null && overrides.length > 0) {
        await apiPut<void>(`/admin/users/${newId}/overrides`, { overrides });
      }
      // If the backend hands back a one-time temp password, surface it once.
      const temp = created?.temp_password ?? createdUser?.temp_password;
      if (typeof temp === 'string' && temp) emit('temp-password', temp);
      // Contract sect 3: welcome_sent reports whether the opt-in mail went out.
      if (sendWelcome.value) {
        emit('welcome-result', { sent: created?.welcome_sent === true, email: email.value.trim() });
      }
    } else {
      const id = props.userId as number;
      await apiPatch<void>(`/admin/users/${id}`, {
        name: name.value.trim(),
        active: active.value,
        goto_extension: gotoExtension.value.trim() || null,
        person_id: personId.value,
      });
      await apiPut<void>(`/admin/users/${id}/roles`, { roles: selectedRoles.value });
      await apiPut<void>(`/admin/users/${id}/overrides`, { overrides: overridesPayload() });
    }
    emit('saved');
    emit('close');
  } catch (e) {
    errorMessage.value = e instanceof ApiError
      ? (e.detail ?? `Save failed (${e.status}${e.code ? ` ${e.code}` : ''}).`)
      : 'Save failed.';
  } finally {
    saving.value = false;
  }
}

const OVERRIDE_LABELS: Record<string, string> = {
  data_entry: 'Data Entry',
  reporting_view: 'View Reporting',
  assisted_entry_review: 'Entry Queue (assisted-entry review)',
  recordings_listen: 'Listen to Recordings',
};
</script>

<template>
  <BaseModal
    :open="open"
    :title="isCreate ? 'Add user' : `Edit user — ${name || email}`"
    max-width="md:max-w-3xl"
    @close="emit('close')"
  >
    <!-- Tab strip (pattern: firstline SubTabNav) -->
    <nav class="flex gap-px overflow-x-auto border-b border-border-c -mx-4 md:-mx-5 px-2 md:px-3 mb-4">
      <button
        v-for="t in visibleTabs"
        :key="t.key"
        type="button"
        class="px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors min-h-[44px]"
        :class="tab === t.key
          ? 'text-accent border-b-2 border-accent'
          : 'text-fg-muted hover:text-fg border-b-2 border-transparent'"
        @click="tab = t.key"
      >
        {{ t.label }}
      </button>
    </nav>

    <div v-if="errorMessage" class="alert-error mb-4" role="alert">{{ errorMessage }}</div>
    <div v-if="loading" class="space-y-3 py-2" aria-label="Loading user">
      <div class="skeleton h-12 w-full" />
      <div class="skeleton h-12 w-full" />
      <div class="skeleton h-12 w-2/3" />
    </div>

    <template v-else>
      <!-- PROFILE -->
      <div v-show="tab === 'profile'" class="space-y-4">
        <div>
          <label class="field-label" for="ue-name">Name</label>
          <input id="ue-name" v-model="name" class="field-input" type="text" autocomplete="off" />
        </div>
        <div>
          <label class="field-label" for="ue-email">Email (login identifier)</label>
          <input
            id="ue-email" v-model="email" class="field-input" type="email" autocomplete="off"
            autocapitalize="none" spellcheck="false" :disabled="!isCreate"
          />
          <p v-if="!isCreate" class="mt-1 text-xs text-fg-muted">Email changes are not part of the v1 admin surface.</p>
        </div>
        <div>
          <label class="field-label" for="ue-goto">GoTo extension (optional) <HelpTip k="admin.goto_extension" /></label>
          <input id="ue-goto" v-model="gotoExtension" class="field-input" type="text" autocomplete="off" />
        </div>
        <div>
          <label class="field-label" for="ue-person">Linked person (staff identity, optional) <HelpTip k="admin.linked_person" /></label>
          <select id="ue-person" v-model="personId" class="field-input">
            <option :value="null">— no person link —</option>
            <option v-for="p in people" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <label v-if="!isCreate" class="flex items-center gap-3 py-2 min-h-[44px] cursor-pointer">
          <input v-model="active" type="checkbox" class="w-5 h-5 accent-[var(--accent)]" />
          <span class="text-sm text-fg">Active (unchecking deactivates on save)</span>
        </label>
        <label v-if="isCreate" class="flex items-center gap-3 py-2 min-h-[44px] cursor-pointer">
          <input v-model="sendWelcome" type="checkbox" class="w-5 h-5 accent-[var(--accent)]" />
          <span class="text-sm text-fg">
            Send welcome email
            <span class="block text-xs text-fg-muted">
              Emails the portal link and temporary password to this address after creation.
            </span>
          </span>
        </label>
        <p v-if="isCreate" class="text-xs text-fg-muted">
          New users are created with a temporary password and must change it on first login.
        </p>
      </div>

      <!-- ROLES: multi-select + live union preview -->
      <div v-show="tab === 'roles'" class="space-y-4">
        <p class="text-sm text-fg-muted">
          A user holds a <span class="font-semibold text-fg">set</span> of roles — effective capabilities
          are the union across every selected role.
        </p>
        <div class="space-y-2">
          <label
            v-for="r in roles"
            :key="r.key"
            class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer min-h-[48px] transition-colors"
            :class="selectedRoles.includes(r.key) ? 'border-accent bg-accent/5' : 'border-border-c hover:bg-input'"
          >
            <input
              type="checkbox"
              class="w-5 h-5 mt-0.5 accent-[var(--accent)]"
              :checked="selectedRoles.includes(r.key)"
              @change="toggleRole(r.key)"
            />
            <span>
              <span class="block text-sm font-semibold text-fg">{{ r.label }}</span>
              <span v-if="r.description" class="block text-xs text-fg-muted mt-0.5">{{ r.description }}</span>
              <span v-if="r.capabilities.length" class="block text-xs text-fg-muted mt-1">
                Grants: <code class="text-accent">{{ r.capabilities.join(', ') }}</code>
              </span>
            </span>
          </label>
        </div>
        <div class="bg-input/50 border border-border-c rounded-lg p-3">
          <div class="text-xs uppercase tracking-wide text-fg-muted mb-1.5">Live union preview</div>
          <div v-if="roleUnion.length" class="flex flex-wrap gap-1.5">
            <code v-for="c in roleUnion" :key="c" class="text-xs px-2 py-0.5 rounded bg-card border border-border-c text-fg">{{ c }}</code>
          </div>
          <p v-else class="text-xs text-fg-muted">No roles selected — no role-derived capabilities.</p>
        </div>
      </div>

      <!-- ACCESS: three-state overrides + effective preview -->
      <div v-show="tab === 'access'" class="space-y-4">
        <p class="text-sm text-fg-muted">
          Per-user overrides on top of the role union. <span class="font-semibold text-fg">Deny wins.</span>
        </p>
        <div class="space-y-3">
          <div
            v-for="cap in OVERRIDABLE_CAPS"
            :key="cap"
            class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg border border-border-c"
          >
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-fg">{{ OVERRIDE_LABELS[cap] ?? cap }}</div>
              <div class="text-xs text-fg-muted">
                <code>{{ cap }}</code>
                <span v-if="roleUnion.includes(cap)"> · in role union</span>
              </div>
            </div>
            <div class="grid grid-cols-3 rounded-lg border border-border-c overflow-hidden text-sm">
              <button
                v-for="state in (['inherit', 'grant', 'deny'] as const)"
                :key="state"
                type="button"
                class="px-3 py-2.5 min-h-[44px] capitalize transition-colors"
                :class="overrideState[cap] === state
                  ? state === 'deny'
                    ? 'bg-accent-bad/20 text-accent-bad font-semibold'
                    : state === 'grant'
                      ? 'bg-accent-good/20 text-accent-good font-semibold'
                      : 'bg-input text-fg font-semibold'
                  : 'text-fg-muted hover:bg-input'"
                @click="overrideState[cap] = state"
              >
                {{ state }}
              </button>
            </div>
          </div>
        </div>
        <div class="bg-input/50 border border-border-c rounded-lg p-3">
          <div class="text-xs uppercase tracking-wide text-fg-muted mb-1.5">Effective capabilities (preview)</div>
          <div v-if="effectivePreview.length" class="flex flex-wrap gap-1.5">
            <code v-for="c in effectivePreview" :key="c" class="text-xs px-2 py-0.5 rounded bg-card border border-border-c text-fg">{{ c }}</code>
          </div>
          <p v-else class="text-xs text-fg-muted">No effective capabilities.</p>
          <p v-if="!isCreate && serverEffectiveCaps.length" class="mt-2 text-xs text-fg-muted">
            Server-computed now: <code>{{ serverEffectiveCaps.join(', ') }}</code>
          </p>
        </div>
      </div>

      <!-- AUDIT: this user's recent auth events -->
      <div v-show="tab === 'audit'">
        <div v-if="auditEvents.length === 0" class="text-sm text-fg-muted py-6 text-center">
          No recent auth events for this user.
        </div>
        <div v-else class="overflow-x-auto border border-border-c rounded-lg">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs uppercase tracking-wide text-fg-muted border-b border-border-c">
                <th class="px-3 py-2.5 font-medium">When</th>
                <th class="px-3 py-2.5 font-medium">Event</th>
                <th class="px-3 py-2.5 font-medium">IP</th>
                <th class="px-3 py-2.5 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ev in auditEvents" :key="ev.id" class="border-b border-border-c/60 last:border-0">
                <td class="px-3 py-2 text-fg whitespace-nowrap">{{ fmtDateTime(ev.at) }}</td>
                <td class="px-3 py-2"><code class="text-xs text-accent">{{ ev.event }}</code></td>
                <td class="px-3 py-2 text-fg-muted whitespace-nowrap">{{ ev.ip ?? '—' }}</td>
                <td class="px-3 py-2 text-fg-muted">{{ fmtDetail(ev.detail) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex gap-3 justify-end">
        <button type="button" class="btn-secondary" :disabled="saving" @click="emit('close')">Cancel</button>
        <button type="button" class="btn-primary" :disabled="saving || loading" @click="save">
          {{ saving ? 'Saving…' : isCreate ? 'Create user' : 'Save changes' }}
        </button>
      </div>
    </template>
  </BaseModal>
</template>
